/**
 * Performance Monitoring Utilities
 *
 * Industry best practices for performance monitoring:
 * - Web Vitals tracking (Core Web Vitals)
 * - Custom performance metrics
 * - Performance budget monitoring
 * - Bundle size tracking
 */

// Import web-vitals functions - version 2.1.4 API uses get* functions
import { getCLS, getFID, getFCP, getLCP, getTTFB, Metric } from "web-vitals";
import * as Sentry from "@sentry/react";
import { getRuntimeMode } from "../config/env";

/**
 * Performance thresholds (industry standards)
 */
const PERFORMANCE_THRESHOLDS = {
  // Core Web Vitals thresholds
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  FID: { good: 100, poor: 300 }, // First Input Delay (ms)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift (score)
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
  // INP: { good: 200, poor: 500 }, // Interaction to Next Paint (ms) - requires web-vitals 3.0+
} as const;

/**
 * Performance metric data
 */
interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta?: number;
  id: string;
  navigationType?: string;
}

/**
 * Send performance metric to monitoring service
 */
function sendToAnalytics(metric: PerformanceMetric): void {
  // Send to Sentry for error tracking and performance monitoring
  const mode = getRuntimeMode();
  if (mode === "production") {
    try {
      // Use Sentry metrics API (v10+)
      Sentry.metrics.distribution(metric.name, metric.value, {
        unit: "millisecond",
      });

      // Add tags via breadcrumb for context
      Sentry.addBreadcrumb({
        category: "performance",
        message: `${metric.name}: ${metric.value.toFixed(2)}ms (${
          metric.rating
        })`,
        level: metric.rating === "poor" ? "warning" : "info",
        data: {
          value: metric.value,
          rating: metric.rating,
          navigationType: metric.navigationType || "navigate",
        },
      });
    } catch (error) {
      // Fail silently if Sentry is not configured
      console.warn("[Performance] Failed to send metric to Sentry:", error);
    }
  }

  // Log to console in development
  if (mode === "development") {
    const emoji =
      metric.rating === "good"
        ? "✅"
        : metric.rating === "needs-improvement"
        ? "⚠️"
        : "❌";
    console.log(
      `${emoji} [Performance] ${metric.name}: ${metric.value.toFixed(2)}ms (${
        metric.rating
      })`
    );
  }
}

/**
 * Determine rating based on thresholds
 */
function getRating(
  name: string,
  value: number
): "good" | "needs-improvement" | "poor" {
  const threshold =
    PERFORMANCE_THRESHOLDS[name as keyof typeof PERFORMANCE_THRESHOLDS];
  if (!threshold) {
    return "good"; // Unknown metrics default to good
  }

  if (value <= threshold.good) {
    return "good";
  } else if (value <= threshold.poor) {
    return "needs-improvement";
  } else {
    return "poor";
  }
}

/**
 * Initialize Web Vitals monitoring
 * Industry best practice: Track Core Web Vitals for SEO and UX
 */
export function initWebVitals(): void {
  // Largest Contentful Paint (LCP)
  getLCP((metric: Metric) => {
    sendToAnalytics({
      name: "LCP",
      value: metric.value,
      rating: getRating("LCP", metric.value),
      delta: metric.delta,
      id: metric.id,
      navigationType: "navigate", // web-vitals 2.1.4 doesn't expose navigationType
    });
  });

  // First Input Delay (FID) - deprecated, use INP instead
  // Keeping for backward compatibility
  getFID((metric: Metric) => {
    sendToAnalytics({
      name: "FID",
      value: metric.value,
      rating: getRating("FID", metric.value),
      delta: metric.delta,
      id: metric.id,
      navigationType: "navigate",
    });
  });

  // Cumulative Layout Shift (CLS)
  getCLS((metric: Metric) => {
    sendToAnalytics({
      name: "CLS",
      value: metric.value,
      rating: getRating("CLS", metric.value),
      delta: metric.delta,
      id: metric.id,
      navigationType: "navigate",
    });
  });

  // First Contentful Paint (FCP)
  getFCP((metric: Metric) => {
    sendToAnalytics({
      name: "FCP",
      value: metric.value,
      rating: getRating("FCP", metric.value),
      delta: metric.delta,
      id: metric.id,
      navigationType: "navigate",
    });
  });

  // Time to First Byte (TTFB)
  getTTFB((metric: Metric) => {
    sendToAnalytics({
      name: "TTFB",
      value: metric.value,
      rating: getRating("TTFB", metric.value),
      delta: metric.delta,
      id: metric.id,
      navigationType: "navigate",
    });
  });

  // Note: INP (Interaction to Next Paint) requires web-vitals 3.0+
  // For now, we track FID which is still supported
  // To add INP support, upgrade to web-vitals 3.0+ and use getINP()
}

/**
 * Measure custom performance metric
 * Use this for measuring specific operations
 */
export function measurePerformance(
  name: string,
  fn: () => void | Promise<void>
): Promise<number> {
  return new Promise(async (resolve) => {
    const start = performance.now();
    const mode = getRuntimeMode();

    try {
      await fn();
    } catch (error) {
      console.error(`[Performance] Error in ${name}:`, error);
    } finally {
      const duration = performance.now() - start;

      // Send to analytics
      if (mode === "production") {
        try {
          Sentry.metrics.distribution(`custom.${name}`, duration, {
            unit: "millisecond",
          });
        } catch (error) {
          // Fail silently if Sentry is not configured
        }
      }

      // Log in development
      if (mode === "development") {
        console.log(`[Performance] ${name}: ${duration.toFixed(2)}ms`);
      }

      resolve(duration);
    }
  });
}

/**
 * Measure React component render performance
 */
export function measureComponentRender(componentName: string): () => void {
  const start = performance.now();
  const markName = `component-render-${componentName}-${Date.now()}`;
  const mode = getRuntimeMode();

  performance.mark(`${markName}-start`);

  return () => {
    performance.mark(`${markName}-end`);
    performance.measure(`${markName}`, `${markName}-start`, `${markName}-end`);

    const measure = performance.getEntriesByName(`${markName}`)[0];
    const duration = measure.duration;

    // Send to analytics
    if (mode === "production") {
      try {
        Sentry.metrics.distribution(
          `component.render.${componentName}`,
          duration,
          {
            unit: "millisecond",
          }
        );
      } catch (error) {
        // Fail silently
      }
    }

    // Log in development
    if (mode === "development") {
      console.log(
        `[Performance] ${componentName} render: ${duration.toFixed(2)}ms`
      );
    }

    // Clean up
    performance.clearMarks(`${markName}-start`);
    performance.clearMarks(`${markName}-end`);
    performance.clearMeasures(`${markName}`);
  };
}

/**
 * Get current performance metrics summary
 */
export function getPerformanceSummary(): {
  metrics: PerformanceMetric[];
  averageScore: number;
} {
  const entries = performance.getEntriesByType("measure");
  const metrics: PerformanceMetric[] = [];

  entries.forEach((entry) => {
    if (entry.name.startsWith("component.render.")) {
      const componentName = entry.name.replace("component.render.", "");
      metrics.push({
        name: componentName,
        value: entry.duration,
        rating:
          entry.duration < 16
            ? "good"
            : entry.duration < 50
            ? "needs-improvement"
            : "poor",
        id: entry.name,
      });
    }
  });

  const averageScore =
    metrics.length > 0
      ? metrics.reduce(
          (sum, m) =>
            sum +
            (m.rating === "good"
              ? 100
              : m.rating === "needs-improvement"
              ? 50
              : 0),
          0
        ) / metrics.length
      : 100;

  return { metrics, averageScore };
}
